import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { toast } from 'react-hot-toast';
import { fetchPlanes, redirectToCheckout, cancelSubscription } from '../services/paymentService';
import { 
  Crown, Users, MessageSquare, CheckCircle, X, 
  Sparkles, ChevronRight, Clock, AlertTriangle, Loader2, ExternalLink 
} from 'lucide-react';

// Formatear precio CLP
const formatPrice = (price) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price);
};

// Componente de barra de progreso
const UsageBar = ({ label, used, limit, icon: Icon, color = 'teal' }) => {
  const percentage = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const isNearLimit = percentage >= 80;
  const isAtLimit = percentage >= 100;
  
  const barColor = isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-amber-500' : `bg-[#2A9D8F]`;
  
  return (
    <div className="bg-white rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${isAtLimit ? 'text-red-500' : 'text-[#2A9D8F]'}`} />
          <span className="font-medium text-gray-700">{label}</span>
        </div>
        <span className={`text-sm font-semibold ${isAtLimit ? 'text-red-600' : 'text-gray-600'}`}>
          {used} de {limit}
        </span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${barColor} transition-all duration-500 rounded-full`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {isAtLimit && (
        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          Límite alcanzado
        </p>
      )}
    </div>
  );
};

// Tarjeta de plan
const PlanCard = ({ plan, currentPlanSlug, currentTier, onUpgrade, onManage, isLoading, loadingSlug, hasSubscription, isManaging }) => {
  const isCurrentPlan = plan.slug === currentPlanSlug;
  const isUpgrade = plan.tier > currentTier;
  const isLoadingThis = isLoading && loadingSlug === plan.slug;
  
  return (
    <div className={`
      relative bg-white rounded-2xl shadow-sm border-2 transition-all duration-300
      ${plan.popular ? 'border-[#2A9D8F] shadow-lg scale-[1.02]' : 'border-gray-100'}
      ${isCurrentPlan ? 'ring-2 ring-[#2A9D8F] ring-offset-2' : ''}
      hover:shadow-md
    `}>
      {/* Badge Popular */}
      {plan.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-[#2A9D8F] text-white px-4 py-1 rounded-full text-xs font-semibold">
            Más Popular
          </span>
        </div>
      )}
      
      {/* Badge Plan Actual */}
      {isCurrentPlan && (
        <div className="absolute -top-3 right-4">
          <span className="bg-gray-800 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
            <Crown className="w-3 h-3" />
            Tu Plan
          </span>
        </div>
      )}
      
      <div className="p-6">
        {/* Header */}
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-800">{plan.name || plan.nombre}</h3>
          <p className="text-sm text-gray-500 mt-1">{plan.description || plan.descripcion}</p>
          <div className="mt-4">
            <span className="text-3xl font-bold text-gray-900">{formatPrice(plan.precio)}</span>
            <span className="text-gray-500">/mes</span>
          </div>
        </div>
        
        {/* Features */}
        <ul className="space-y-3 mb-6">
          <li className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
            <span className="text-gray-700">{plan.max_usuarios} usuario{plan.max_usuarios > 1 ? 's' : ''}</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
            <span className="text-gray-700">{plan.limite_whatsapp} mensajes WhatsApp/mes</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
            <span className="text-gray-700">Gestión de pacientes</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
            <span className="text-gray-700">Agenda de citas</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
            <span className="text-gray-700">Fichas clínicas</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
            <span className="text-gray-700">Inventario básico</span>
          </li>
          <li className="flex items-center gap-2">
            {plan.incluye_inventario_avanzado ? (
              <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
            ) : (
              <X className="w-5 h-5 text-gray-300 flex-shrink-0" />
            )}
            <span className={plan.incluye_inventario_avanzado ? 'text-gray-700' : 'text-gray-400'}>
              Alertas de stock crítico
            </span>
          </li>
          <li className="flex items-center gap-2">
            {plan.incluye_dashboard_financiero ? (
              <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
            ) : (
              <X className="w-5 h-5 text-gray-300 flex-shrink-0" />
            )}
            <span className={plan.incluye_dashboard_financiero ? 'text-gray-700' : 'text-gray-400'}>
              Dashboard financiero
            </span>
          </li>
          <li className="flex items-center gap-2">
            {plan.soporte_prioritario ? (
              <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
            ) : (
              <X className="w-5 h-5 text-gray-300 flex-shrink-0" />
            )}
            <span className={plan.soporte_prioritario ? 'text-gray-700' : 'text-gray-400'}>
              Soporte prioritario
            </span>
          </li>
        </ul>
        
        {/* CTA Button */}
        {isCurrentPlan ? (
          <div className="space-y-2">
            <button 
              disabled
              className="w-full py-3 px-4 rounded-xl bg-gray-100 text-gray-500 font-semibold cursor-not-allowed"
            >
              Plan Actual
            </button>
            {hasSubscription && (
              <button
                onClick={onManage}
                disabled={isManaging}
                className="w-full py-2 px-4 rounded-xl border-2 border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 text-sm"
              >
                {isManaging ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Cargando...
                  </>
                ) : (
                  <>
                    <ExternalLink className="w-4 h-4" />
                    Cancelar Suscripción
                  </>
                )}
              </button>
            )}
          </div>
        ) : isUpgrade ? (
          <button 
            onClick={() => onUpgrade(plan)}
            disabled={isLoading}
            className={`w-full py-3 px-4 rounded-xl text-white font-semibold transition-colors flex items-center justify-center gap-2 ${
              isLoading 
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-[#2A9D8F] hover:bg-[#23877a]'
            }`}
          >
            {isLoadingThis ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Procesando...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Mejorar Plan
              </>
            )}
          </button>
        ) : (
          <a
            href="mailto:soporte@estetikflow.com?subject=Cambio de plan"
            className="w-full py-3 px-4 rounded-xl border-2 border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
          >
            Contactar para Downgrade
          </a>
        )}
      </div>
    </div>
  );
};

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser: user } = useAuth();
  const { planName, subscriptionStatus, isTrial } = usePermissions();
  
  const [isLoading, setIsLoading] = useState(false);
  const [loadingSlug, setLoadingSlug] = useState(null);
  const [isManaging, setIsManaging] = useState(false);
  const [apiPlanes, setApiPlanes] = useState([]);
  const [billingCycle, setBillingCycle] = useState('mensual'); // 'mensual' or 'anual'
  
  const cuenta = user?.cuenta || {};
  const usageStats = cuenta.usage_stats || {};
  const planInfo = cuenta.plan_info || {};
  
  // Determinar el tier actual basado en el id del plan. Para simplificar, comparamos el precio
  const currentPlanSlug = planInfo?.slug || '';
  const currentPlanInfo = apiPlanes.find(p => p.slug === currentPlanSlug);
  const currentTier = currentPlanInfo ? currentPlanInfo.precio : 0;
  
  // Load plans from API
  React.useEffect(() => {
    const loadPlanes = async () => {
      try {
        const data = await fetchPlanes();
        // Agregamos un tier falso basado en precio, y seteamos 'popular' temporalmente
        const enriched = data.map(p => ({
          ...p,
          tier: p.precio, // usamos precio para ver si es upgrade
          description: p.nombre === 'Orden Digital' ? 'Para profesionales independientes' :
                       p.nombre === 'Imperio Profesional' ? 'Para clínicas en crecimiento' : 'Para equipos y múltiples sedes',
          popular: p.nombre === 'Imperio Profesional'
        }));
        setApiPlanes(enriched);
      } catch (err) {
        toast.error('No se pudieron cargar los planes');
      }
    };
    loadPlanes();
  }, []);

  const visiblePlanes = apiPlanes.filter(p => {
    // Check all possible lowercase variants of frecuencia
    const freq = (p.frecuencia || '').toLowerCase().trim();
    if (billingCycle === 'mensual') {
      return freq === 'mensual' || freq === 'month';
    } else {
      return freq === 'anual' || freq === 'year';
    }
  });
  
  // Check for success callbacks
  React.useEffect(() => {
    const status = searchParams.get('status');
    const token = searchParams.get('token');
    
    if (status === 'success') {
      toast.success(
        '¡Gracias por tu pago! Tu plan ha sido renovado exitosamente y activado en el sistema.',
        { duration: 5000, icon: '🎉' }
      );
      // Flow redirige de vuelta aquí automáticamente
      navigate('/app/suscripcion', { replace: true });
    }
  }, [searchParams, navigate]);
  
  // Status badge styling
  const getStatusBadge = () => {
    switch (subscriptionStatus) {
      case 'active':
        return { text: 'Activo', color: 'bg-green-100 text-green-700', icon: CheckCircle };
      case 'trial':
        return { text: 'Período de Prueba', color: 'bg-amber-100 text-amber-700', icon: Clock };
      case 'past_due':
        return { text: 'Pago Pendiente', color: 'bg-red-100 text-red-700', icon: AlertTriangle };
      case 'canceled':
        return { text: 'Cancelado', color: 'bg-gray-100 text-gray-700', icon: X };
      default:
        return { text: 'Sin Plan', color: 'bg-gray-100 text-gray-500', icon: Clock };
    }
  };
  
  const statusBadge = getStatusBadge();
  const StatusIcon = statusBadge.icon;
  
  const handleUpgrade = async (plan) => {
    try {
      setIsLoading(true);
      setLoadingSlug(plan.slug);
      
      // Call backend to create checkout session
      await redirectToCheckout(plan.id);
      
      // Note: If successful, user will be redirected to LemonSqueezy
      // This code won't execute after redirect
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.message || 'Error al procesar el pago');
    } finally {
      setIsLoading(false);
      setLoadingSlug(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!window.confirm('¿Estás seguro de que deseas cancelar tu suscripción? Perderás el acceso a las funciones premium al finalizar tu ciclo de facturación actual.')) {
      return;
    }
    
    try {
      setIsManaging(true);
      const response = await cancelSubscription();
      toast.success(response.message || 'Suscripción cancelada correctamente');
      // Refresh logic would go here if needed
      window.location.reload();
    } catch (error) {
      console.error('Cancel subscription error:', error);
      toast.error(error.message || 'Error al cancelar la suscripción');
    } finally {
      setIsManaging(false);
    }
  };

  // Verificar si el usuario tiene suscripción activa en Flow
  const hasFlowSubscription = !!cuenta.flow_subscription_id;

  // Calcular días restantes con prioridad:
  // 1. Fecha de suscripción (LemonSqueezy)
  // 2. Fecha de registro + 14 días (Trial antiguo)
  const calculateDaysRemaining = () => {
    let expirationDate;

    // Prioridad 1: Fecha de fin de suscripción real
    if (cuenta.subscription_ends_at) {
      expirationDate = new Date(cuenta.subscription_ends_at);
    } else {
      // Prioridad 2: Fallback a trial por registro (fecha creación + 14 días)
      // Usamos fecha de creación de la cuenta
      const createdDate = cuenta.creado_en ? new Date(cuenta.creado_en) : new Date();
      expirationDate = new Date(createdDate);
      expirationDate.setDate(createdDate.getDate() + 14);
    }

    const now = new Date();
    const difference = expirationDate - now;
    const days = Math.ceil(difference / (1000 * 60 * 60 * 24));

    return days > 0 ? days : 0;
  };

  const remainingDays = calculateDaysRemaining();
  // Mostrar mensaje de prueba solo si está en trial Y no es una suscripción pagada activa
  // (Aunque LemonSqueezy pone trial, para nosotros es "Periodo de Prueba Premium")
  const showTrialMessage = isTrial && remainingDays > 0;

  return (
    <div className="p-4 sm:p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Mi Suscripción</h1>
        <p className="text-sm text-gray-500 mt-1">
          Gestiona tu plan y revisa el uso de recursos
        </p>
      </div>
      
      {/* Estado Actual */}
      <div className="bg-gradient-to-r from-[#2A9D8F] to-[#227f74] rounded-2xl p-6 text-white shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-white/70 text-sm">Plan Actual</p>
            <h2 className="text-2xl font-bold mt-1">
              {planName || 'Sin Plan Asignado'}
            </h2>
            {cuenta.nombre && (
              <p className="text-white/80 text-sm mt-1">{cuenta.nombre}</p>
            )}
          </div>
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${statusBadge.color}`}>
            <StatusIcon className="w-4 h-4" />
            <span className="font-semibold text-sm">{statusBadge.text}</span>
          </div>
        </div>
        
        {showTrialMessage && (
          <div className="mt-4 bg-white/10 rounded-lg p-3">
            <p className="text-sm">
              <Clock className="w-4 h-4 inline mr-1" />
              Quedan <strong>{remainingDays} días</strong> de prueba gratuita
            </p>
          </div>
        )}
      </div>
      
      {/* Uso de Recursos */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Uso de Recursos</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <UsageBar 
            label="Usuarios"
            used={usageStats.users_used || 0}
            limit={usageStats.users_limit || 1}
            icon={Users}
          />
          <UsageBar 
            label="Mensajes WhatsApp"
            used={usageStats.whatsapp_used || 0}
            limit={usageStats.whatsapp_limit || 0}
            icon={MessageSquare}
          />
        </div>
      </div>
      
      {/* Planes Disponibles */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <h3 className="text-lg font-semibold text-gray-800">Planes Disponibles</h3>
          
          {/* Toggle de facturación */}
          <div className="bg-gray-200 p-1 rounded-xl inline-flex self-start sm:self-auto">
            <button
              onClick={() => setBillingCycle('mensual')}
              className={`px-6 py-2 rounded-lg text-sm font-semibold transition-all ${
                billingCycle === 'mensual' 
                  ? 'bg-white text-gray-800 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Mensual
            </button>
            <button
              onClick={() => setBillingCycle('anual')}
              className={`px-6 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                billingCycle === 'anual' 
                  ? 'bg-[#2A9D8F] text-white shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Anual
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                billingCycle === 'anual' ? 'bg-white/20 text-white' : 'bg-[#2A9D8F]/10 text-[#2A9D8F]'
              }`}>
                -15%
              </span>
            </button>
          </div>
        </div>

        {apiPlanes.length === 0 ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#2A9D8F] animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {visiblePlanes.map((plan) => (
              <PlanCard 
                key={plan.id}
                plan={plan}
                currentPlanSlug={currentPlanSlug}
                currentTier={currentTier}
                onUpgrade={handleUpgrade}
                onManage={handleManageSubscription}
                isLoading={isLoading}
                loadingSlug={loadingSlug}
                hasSubscription={hasFlowSubscription}
                isManaging={isManaging}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Footer Help */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h4 className="font-semibold text-gray-800">¿Necesitas ayuda?</h4>
            <p className="text-sm text-gray-500 mt-1">
              Contáctanos para resolver dudas sobre tu suscripción
            </p>
          </div>
          <a
            href="mailto:soporte@estetikflow.com"
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
          >
            Contactar Soporte
            <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
